import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Tag,
  Layout,
  Alert,
  Modal,
  message,
  Input,
  Select,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CalculatorOutlined,
  HistoryOutlined,
  SearchOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  TableOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  getAllSavedProjects,
  transformToPayload,
  processTenderFromAPI,
  transformFromPayload,
  saveTenderToStorage,
} from "../../utils/storage";
import { TenderState } from "../../types/tender.types";
import {
  useBillOfQuantities,
  BOQ_QUERY_KEYS,
} from "../../hooks/useBillOfQuantities";
import { billOfQuantitiesApi } from "../../api/billOfQuantities";
import { useTender } from "../../contexts/TenderContext";
import { useDevModeContext } from "../../contexts/DevModeContext";
// import { useChangeDetectionContext } from "../../contexts/ChangeDetectionContext";
import { useUserRole, AVAILABLE_REGIONS } from "../../hooks/useUserRole";
import { useAuth } from "../../hooks/useAuth";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import MJ_LOGO from "@/assets/mnj-logo.jpg";
import AuthButton from "../auth/AuthButton";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { EnvironmentBadge } from "../common/EnvironmentBadge";
import { Layers3, Route } from "lucide-react";

const { Title } = Typography;
const { Header: AntHeader } = Layout;

const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

// Tenders created on these fixed calendar dates are permanently hidden from
// the dashboard. Dates, not a rolling "today/yesterday" — they stay fixed
// regardless of when the app is opened.
const HIDDEN_CREATION_DATES = ["2026-08-05", "2026-08-06"];

// Feature flag, explicit opt-in (VITE_HIDE_TENDER_DATES === 'true'): off for
// `npm run builddev` (.env.development), on for `npm run builduat`/`buildprod`
// (.env.uat / .env.production), so the hidden-date filter below never applies
// to local/QA testing.
const hideTenderDatesEnabled =
  import.meta.env.VITE_HIDE_TENDER_DATES === "true";

// Local (device) calendar date of an ISO timestamp, as "YYYY-MM-DD".
function toLocalDateKey(isoDate: string): string {
  const d = new Date(isoDate);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

interface OpportunityRecord {
  key: string;
  no?: string;
  tenderId: string;
  tender: string;
  client: string;
  region: string;
  totalBudget: number;
  rsBudget: number;
  gwBudget: number;
  creationDate: string;
  lastUpdateDate: string;
  status: "In-Progress" | "Submitted" | "Approved";
  creator: string;
  lastModifiedBy: string;
  opportunityNo?: string;
  jobTypes?: { groundworks: boolean; roadsAndSewers: boolean };
}

const OpportunitiesDashboard: React.FC = () => {
  const { devMode: showDevMode, setDevMode } = useDevModeContext();
  // Scroll to top on page load
  useScrollToTop();

  // Landing on the dashboard means leaving whatever tender/project was open -
  // reset dev mode so it doesn't silently carry over into the next tender.
  useEffect(() => {
    setDevMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { dispatch, resetTender, setViewMode } = useTender();
  // const { clearAllChanges } = useChangeDetectionContext();
  const { region: userRegion, role: userRole } = useUserRole(); // Get user's region and role
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const { user } = useAuth(); // Get logged-in user

  // Prefetch Item Library in background when region is available
  // This caches the data so GroundworksReview doesn't need to fetch it again
  const { isLoading: isLoadingItemLibrary } = useItemLibrary(
    userRegion || "",
    undefined,
    {
      enabled: !!userRegion, // Only fetch when region is available
    },
  );

  // Debug logging
  useEffect(() => {
    if (userRegion) {
    }
  }, [userRegion]);

  const [opportunities, setOpportunities] = useState<OpportunityRecord[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<OpportunityRecord | null>(null);
  const [actionType, setActionType] = useState<"edit" | "view" | "review">(
    "edit",
  );
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDownloadButtonHovered, setIsDownloadButtonHovered] =
    useState(false);
  const [isDownloadingGroundworks, setIsDownloadingGroundworks] =
    useState(false);
  const [isDownloadingRoadsAndSewers, setIsDownloadingRoadsAndSewers] =
    useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [jobTypeFilter, setJobTypeFilter] = useState<string>("all");
  const [showMyActions, setShowMyActions] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingRecord, setDeletingRecord] =
    useState<OpportunityRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Debug: Log when userRegion changes
  useEffect(() => {}, [userRegion]);

  // Sync selectedRegion from userRegion on initial load
  useEffect(() => {
    if (userRegion !== undefined && selectedRegion === null) {
      setSelectedRegion(userRegion);
    }
  }, [userRegion, selectedRegion]);

  /**
   * Parse region from format "TEST~~REGION" to extract "REGION"
   */
  const parseRegion = (region: string | undefined): string => {
    if (!region) return "West Midlands"; // Default fallback

    // Split by ~~ and take the last part
    const parts = region.split("~~");
    return parts[parts.length - 1] || "West Midlands";
  };

  // Fetch Bill of Quantities data using the hook
  const {
    data: boqData,
    isLoading: boqLoading,
    error: boqError,
  } = useBillOfQuantities();

  // Clear localStorage and reset context when dashboard loads
  useEffect(() => {
    // Clear all tender-related localStorage items
    localStorage.removeItem("currentTender");
    localStorage.removeItem("currentBoqVersion");
    localStorage.removeItem("currentRateCard");
    localStorage.removeItem("isHistoricalVersion"); // Clear historical version flag

    // Clear all project-specific tender data (mjEvans_tender_*)
    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("mjEvans_tender_") ||
        key.startsWith("geoToberWallBuildUp_") ||
        key.startsWith("retainingWallsBuildUp_")
      ) {
        localStorage.removeItem(key);
      }
    });

    // Clear session storage flags
    sessionStorage.removeItem("versionDataReloaded");

    // Reset TenderContext to initial state
    if (resetTender) {
      resetTender();
    }

    // Clear change detection context
    // clearAllChanges();
  }, [resetTender /*, clearAllChanges*/]);

  // Note: Refetch is now handled automatically via React Query cache invalidation
  // in forceSaveBothBoqEndpoints after successful API submissions

  // Debug: Log opportunities state changes
  useEffect(() => {}, [opportunities]);

  // Reset to first page when opportunities data changes
  useEffect(() => {
    if (opportunities.length > 0) {
      setCurrentPage(1);
    }
  }, [opportunities.length]);

  // Debug: Log pagination state changes
  useEffect(() => {}, [currentPage, pageSize, opportunities.length]);

  // Helper function to extract budgets from BOQ data
  // Expected API structure:
  // {
  //   "project": { "name": "...", "client": "...", "region": "..." },
  //   "billOfQuantities": [{
  //     "roadAndSewers": { "totals": { "grandTotal": 0 } },
  //     "groundworks": { "totals": { "grandTotal": 80115.84 } }
  //   }],
  //   "totals": { "tenderTotal": 80115.84 }
  // }
  const extractBudgetsFromBOQ = (boqData: any, projectName: string) => {
    let rsBudget = 0;
    let gwBudget = 0;
    let totalBudget = 0;

    if (!boqData) {
      return { totalBudget: 0, rsBudget: 0, gwBudget: 0 };
    }

    // Handle array of BOQ responses
    if (Array.isArray(boqData)) {
      const projectBOQ = boqData.find(
        (boq: any) => boq.project?.name === projectName,
      );
      if (projectBOQ) {
        // Extract from billOfQuantities array
        if (
          projectBOQ.billOfQuantities &&
          projectBOQ.billOfQuantities.length > 0
        ) {
          const boq = projectBOQ.billOfQuantities[0];
          rsBudget = boq.roadAndSewers?.totals?.grandTotal || 0;
          gwBudget = boq.groundworks?.totals?.grandTotal || 0;
        }
        totalBudget = projectBOQ.totals?.tenderTotal || rsBudget + gwBudget;
      }
    }
    // Handle single BOQ response
    else if (boqData.project?.name === projectName) {
      if (boqData.billOfQuantities && boqData.billOfQuantities.length > 0) {
        const boq = boqData.billOfQuantities[0];
        rsBudget = boq.roadAndSewers?.totals?.grandTotal || 0;
        gwBudget = boq.groundworks?.totals?.grandTotal || 0;
      }
      totalBudget = boqData.totals?.tenderTotal || rsBudget + gwBudget;
    }
    // Handle wrapped response with data property
    else if (boqData.data) {
      return extractBudgetsFromBOQ(boqData.data, projectName);
    }

    return { totalBudget, rsBudget, gwBudget };
  };

  // Helper function to calculate budgets from tender data (fallback)
  const calculateBudgets = (tenderData: TenderState) => {
    let rsBudget = 0;
    let gwBudget = 0;

    // Calculate Roads & Sewers Budget
    if (tenderData.preliminaryItems?.totals?.grandTotal) {
      rsBudget = tenderData.preliminaryItems.totals.grandTotal;
    }

    // Calculate Groundworks Budget
    if (tenderData.groundworks) {
      // Main groundworks total
      if (tenderData.groundworks.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.totals.grandTotal;
      }

      // External works total
      if (tenderData.groundworks.externalWorks?.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.externalWorks.totals.grandTotal;
      }

      // Private drainage total
      if (tenderData.groundworks.privateDrainage?.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.privateDrainage.totals.grandTotal;
      }

      // Additional items total
      if (tenderData.groundworks.additionalItems?.totals?.grandTotal) {
        gwBudget += tenderData.groundworks.additionalItems.totals.grandTotal;
      }
    }

    const totalBudget = rsBudget + gwBudget;

    return { totalBudget, rsBudget, gwBudget };
  };

  // Load opportunities on mount and when BOQ data changes
  useEffect(() => {
    if (boqError) {
    }
    // Always load opportunities, even without BOQ data
    loadOpportunities();
  }, [boqData, boqError]);

  // Initial load on mount
  useEffect(() => {
    loadOpportunities();
  }, []);

  const loadOpportunities = () => {
    try {
      let savedOpportunities: OpportunityRecord[] = [];

      // Primary source: BOQ API data
      if (boqData) {
        // Handle if boqData is wrapped in a data property
        const apiData = boqData.data || boqData;

        // Check if it's an array
        if (Array.isArray(apiData)) {
          savedOpportunities = apiData.map((boqItem: any, index: number) => {
            const projectName = boqItem.project?.name || `Project ${index + 1}`;
            const client = boqItem.project?.client || "-";
            const region = parseRegion(boqItem.project?.region);

            // Extract budgets from billOfQuantities
            let rsBudget = 0;
            let gwBudget = 0;
            let totalBudget = 0;

            if (
              boqItem.billOfQuantities &&
              boqItem.billOfQuantities.length > 0
            ) {
              const boq = boqItem.billOfQuantities[0];
              rsBudget = boq.roadAndSewers?.totals?.grandTotal || 0;
              gwBudget = boq.groundworks?.totals?.grandTotal || 0;
            }

            totalBudget = boqItem.totals?.tenderTotal || rsBudget + gwBudget;

            const apiJobTypes = boqItem["job-types"] || {};
            const jobTypes = {
              groundworks: !!apiJobTypes.groundworks,
              roadsAndSewers: !!apiJobTypes.roadsAndSewers,
            };

            // Use API fields for dates, status, and creator
            let creationDate =
              boqItem.tenderCreatedDate ||
              boqItem.calculatedAt ||
              new Date().toISOString();
            let lastUpdateDate =
              boqItem.lastModified ||
              boqItem.calculatedAt ||
              new Date().toISOString();
            let status = boqItem.status || "Pending";
            let creator = boqItem.createdBy || "Unknown";
            let lastModifiedBy = boqItem.lastModifiedBy || "-";
            let projectId = `project_${index}`;

            // Try to find matching project in localStorage for additional metadata (ID only)
            const allProjects = getAllSavedProjects();
            const matchingProject = allProjects.find(
              (p) => p.projectName === projectName,
            );
            if (matchingProject) {
              projectId = matchingProject.id;
              // Keep dates, status, creator from API
            }

            const opportunity = {
              key: `tender_${projectId}`,
              tenderId: projectId,
              tender: projectName,
              client,
              region,
              totalBudget,
              rsBudget,
              gwBudget,
              creationDate,
              lastUpdateDate,
              status: status as "Pending" | "Submitted" | "Approved",
              creator,
              lastModifiedBy,
              opportunityNo: boqItem.opportunityNo || undefined,
              jobTypes,
            };
            return opportunity;
          });
        } else {
        }
      }

      // Fallback: Use localStorage if no API data
      if (savedOpportunities.length === 0) {
        const projects = getAllSavedProjects();

        savedOpportunities = projects.map((project) => {
          const storageKey = `mjEvans_tender_${project.id}`;
          const storedData = localStorage.getItem(storageKey);

          let totalBudget = 0;
          let rsBudget = 0;
          let gwBudget = 0;
          let status = "Pending";
          let creator = "Unknown";
          let lastModifiedBy = "-";
          let lastModified = project.lastModified.toISOString();
          let createdDate = project.dateCreated.toISOString();
          let jobTypes = { groundworks: false, roadsAndSewers: false };

          if (storedData) {
            try {
              const parsed = JSON.parse(storedData);
              const tenderData: TenderState = parsed.data || parsed;
              const budgets = calculateBudgets(tenderData);
              totalBudget = budgets.totalBudget;
              rsBudget = budgets.rsBudget;
              gwBudget = budgets.gwBudget;
              if (tenderData.jobTypes) {
                jobTypes = {
                  groundworks: !!tenderData.jobTypes.groundworks,
                  roadsAndSewers: !!tenderData.jobTypes.roadsAndSewers,
                };
              }

              // Extract status, creator, and dates from stored data if available
              if (tenderData.status) {
                status = tenderData.status;
              }
              if ((tenderData as any).createdBy) {
                creator = (tenderData as any).createdBy;
              }
              if ((tenderData as any).lastModifiedBy) {
                lastModifiedBy = (tenderData as any).lastModifiedBy;
              }
              if (tenderData.lastModified) {
                lastModified = tenderData.lastModified.toISOString
                  ? tenderData.lastModified.toISOString()
                  : (tenderData.lastModified as any);
              }
              if (tenderData.dateCreated) {
                createdDate = tenderData.dateCreated.toISOString
                  ? tenderData.dateCreated.toISOString()
                  : (tenderData.dateCreated as any);
              }
            } catch (error) {}
          }

          return {
            key: `tender_${project.id}`,
            tenderId: project.id,
            tender: project.projectName,
            client: project.client || "-",
            region: "West Midlands", // Default region for localStorage projects
            totalBudget,
            rsBudget,
            gwBudget,
            creationDate: createdDate,
            lastUpdateDate: lastModified,
            status: status as "Pending" | "Submitted" | "Approved",
            creator,
            lastModifiedBy,
            jobTypes,
          };
        });
      }

      setOpportunities(savedOpportunities);
    } catch (error) {
      setOpportunities([]);
    }
  };

  const handleNewOpportunity = () => {
    // Clear current tender and navigate to tender info
    localStorage.removeItem("currentTender");
    // Clear historical version flag when creating a new tender
    localStorage.removeItem("isHistoricalVersion");
    // New job-types-first flow is gated to localhost + dev mode for now —
    // everywhere else keeps the previous tender-info-first flow.
    navigate("/tender/job-types");
    // if (showDevMode) {
    //   navigate("/tender/job-types");
    // } else {
    //   navigate("/tender-info");
    // }
  };

  const handleEdit = async (record: OpportunityRecord) => {
    // Clear historical version flag when editing a tender (editable mode)
    localStorage.removeItem("isHistoricalVersion");

    try {
      // Fetch tender data from API by project name
      message.loading({ content: "Loading tender data...", key: "loadTender", duration: 0 });

      const response = await billOfQuantitiesApi.getByProject(
        record.tender,
        record.opportunityNo,
      );

      // Handle different response structures
      let apiData = null;

      // Check if response has explicit success: false
      if (response && response.success === false) {
        message.error({
          content: response.message || "Failed to load tender data from API",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Extract data based on response structure
      if (response && response.data) {
        // Standard format: { success: true, data: PayloadFormat }
        apiData = response.data;
      } else if (response && response.project) {
        // Response is PayloadFormat directly (has project field)
        apiData = response;
      } else if (
        response &&
        typeof response === "object" &&
        Object.keys(response).length > 0
      ) {
        // Response is some data object
        apiData = response;
      }

      if (!apiData) {
        message.error({
          content: "No tender data found",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Convert PayloadFormat to TenderState for context update
      const tenderState = transformFromPayload(apiData, {
        id: record.tenderId,
      });

      // Check if there's actual data in the project and set job types
      let hasRoadsAndSewersData = false;
      let hasGroundworksData = false;

      // Check for Roads & Sewers data in the payload
      if (
        apiData.roadAndSewers ||
        apiData.preliminaryItems ||
        apiData.foulSewers ||
        apiData.stormSewers ||
        apiData.roads ||
        apiData.mainServices
      ) {
        hasRoadsAndSewersData = true;
      }

      // Check for Groundworks data in the payload
      if (
        apiData.groundworks ||
        (apiData.cutFills && Object.keys(apiData.cutFills).length > 0) ||
        (apiData.foundations && Object.keys(apiData.foundations).length > 0)
      ) {
        hasGroundworksData = true;
      }

      // Set job types in tenderState before dispatching
      tenderState.jobTypes = {
        roadsAndSewers: hasRoadsAndSewersData || record.rsBudget > 0,
        groundworks: hasGroundworksData || record.gwBudget > 0,
      };

      // Update TenderContext using dispatch
      dispatch({ type: "LOAD_PROJECT", payload: tenderState });

      // Save API data directly to localStorage (API data is already in PayloadFormat)
      const storageKey = `mjEvans_tender_${record.tenderId}`;
      const dataToStore = {
        version: "3.0",
        timestamp: new Date().toISOString(),
        data: apiData, // API data sits inside 'data' key
      };

      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      localStorage.setItem("currentTender", JSON.stringify(dataToStore));

      message.success({
        content: "Tender data loaded successfully",
        key: "loadTender",
        duration: 2,
      });
    } catch (error) {
      message.error({
        content: "Failed to load tender data from API",
        key: "loadTender",
        duration: 3,
      });
      return;
    }

    // Only show modal if both budgets have values
    if (record.rsBudget > 0 && record.gwBudget > 0) {
      setSelectedRecord(record);
      setActionType("edit");
      setIsModalOpen(true);
      return;
    }

    // If only groundworks has value, navigate directly
    if (record.gwBudget > 0) {
      navigate("/groundworks/cut-fills");
      return;
    }

    // If only roads & sewers has value, navigate directly
    if (record.rsBudget > 0) {
      navigate("/roads-sewers/preliminary");
      return;
    }

    // Default: navigate to groundworks if both are 0
    navigate("/groundworks/cut-fills");
  };

  const handleView = async (record: OpportunityRecord) => {
    // Set historical version flag when viewing a tender (read-only mode)
    localStorage.setItem("isHistoricalVersion", "true");

    try {
      // Fetch tender data from API by project name
      message.loading({ content: "Loading tender data...", key: "loadTender", duration: 0 });

      const response = await billOfQuantitiesApi.getByProject(
        record.tender,
        record.opportunityNo,
      );

      // Handle different response structures
      let apiData = null;

      // Check if response has explicit success: false
      if (response && response.success === false) {
        message.error({
          content: response.message || "Failed to load tender data from API",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Extract data based on response structure
      if (response && response.data) {
        // Standard format: { success: true, data: PayloadFormat }
        apiData = response.data;
      } else if (response && response.project) {
        // Response is PayloadFormat directly (has project field)
        apiData = response;
      } else if (
        response &&
        typeof response === "object" &&
        Object.keys(response).length > 0
      ) {
        // Response is some data object
        apiData = response;
      }

      if (!apiData) {
        message.error({
          content: "No tender data found",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Convert PayloadFormat to TenderState for context update
      const tenderState = transformFromPayload(apiData, {
        id: record.tenderId,
      });

      // Check if there's actual data in the project and set job types
      let hasRoadsAndSewersData = false;
      let hasGroundworksData = false;

      // Check for Roads & Sewers data in the payload
      if (
        apiData.roadAndSewers ||
        apiData.preliminaryItems ||
        apiData.foulSewers ||
        apiData.stormSewers ||
        apiData.roads ||
        apiData.mainServices
      ) {
        hasRoadsAndSewersData = true;
      }

      // Check for Groundworks data in the payload
      if (
        apiData.groundworks ||
        (apiData.cutFills && Object.keys(apiData.cutFills).length > 0) ||
        (apiData.foundations && Object.keys(apiData.foundations).length > 0)
      ) {
        hasGroundworksData = true;
      }

      // Set job types in tenderState before dispatching
      tenderState.jobTypes = {
        roadsAndSewers: hasRoadsAndSewersData || record.rsBudget > 0,
        groundworks: hasGroundworksData || record.gwBudget > 0,
      };

      // Update TenderContext using dispatch
      dispatch({ type: "LOAD_PROJECT", payload: tenderState });

      // Save API data directly to localStorage (API data is already in PayloadFormat)
      const storageKey = `mjEvans_tender_${record.tenderId}`;
      const dataToStore = {
        version: "3.0",
        timestamp: new Date().toISOString(),
        data: apiData, // API data sits inside 'data' key
      };

      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      localStorage.setItem("currentTender", JSON.stringify(dataToStore));

      message.success({
        content: "Tender data loaded successfully",
        key: "loadTender",
        duration: 2,
      });
    } catch (error) {
      message.error({
        content: "Failed to load tender data from API",
        key: "loadTender",
        duration: 3,
      });
      return;
    }

    // Only show modal if both budgets have values
    if (record.rsBudget > 0 && record.gwBudget > 0) {
      setSelectedRecord(record);
      setActionType("view");
      setIsModalOpen(true);
      return;
    }

    // If only groundworks has value, navigate directly
    if (record.gwBudget > 0) {
      setViewMode(true);
      navigate("/groundworks/cut-fills?mode=view");
      return;
    }

    // If only roads & sewers has value, navigate directly
    if (record.rsBudget > 0) {
      setViewMode(true);
      navigate("/roads-sewers/preliminary?mode=view");
      return;
    }

    // Default: navigate to groundworks if both are 0
    setViewMode(true);
    navigate("/groundworks/cut-fills?mode=view");
  };

  const handleJobTypeSelect = (jobType: "roadsAndSewers" | "groundworks") => {
    if (!selectedRecord) return;

    // Load the tender data
    const storageKey = `mjEvans_tender_${selectedRecord.tenderId}`;
    const data = localStorage.getItem(storageKey);
    if (data) {
      localStorage.setItem("currentTender", data);
    }

    // Navigate to appropriate section based on selected job type and action
    if (actionType === "review") {
      // For review action, navigate to review pages
      setViewMode(false);
      // Clear historical version flag for review (editable mode)
      localStorage.removeItem("isHistoricalVersion");
      if (jobType === "groundworks") {
        navigate("/groundworks/review");
      } else {
        navigate("/roads-sewers/review");
      }
    } else if (actionType === "view") {
      // For view action, set view mode and navigate
      setViewMode(true);
      // Set historical version flag for view (read-only mode)
      localStorage.setItem("isHistoricalVersion", "true");
      const modeParam = "?mode=view";
      if (jobType === "groundworks") {
        navigate(`/groundworks/cut-fills${modeParam}`);
      } else {
        navigate(`/roads-sewers/preliminary${modeParam}`);
      }
    } else {
      // For edit action, disable view mode and navigate
      setViewMode(false);
      // Clear historical version flag for edit (editable mode)
      localStorage.removeItem("isHistoricalVersion");
      if (jobType === "groundworks") {
        navigate("/groundworks/cut-fills");
      } else {
        navigate("/roads-sewers/preliminary");
      }
    }

    // Close modal
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };

  // Helper function to download the GWKS Take-off Sheet V7.2.3.xlsx file from public folder
  const downloadGroundworksTemplate = async () => {
    const baseUrl = import.meta.env.BASE_URL || "/";
    const url = `${baseUrl}GWKS Take-off Sheet V7.2.3.xlsx`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download GWKS Take-off Sheet V7.2.3.xlsx`);
    }

    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "GWKS Take-off Sheet V7.2.3.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  // Helper function to download the Roads & Sewers take-off template
  const downloadRoadsAndSewersTemplate = async () => {
    const baseUrl = import.meta.env.BASE_URL || "/";
    const sourceFile = "R&S Take-off sheet.xlsx";
    const url = `${baseUrl}${sourceFile}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download ${sourceFile}`);
    }

    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = sourceFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  const handleDownloadTakeoff = async (
    jobTypeKey: "groundworks" | "roadsAndSewers",
  ) => {
    const setIsDownloading =
      jobTypeKey === "groundworks"
        ? setIsDownloadingGroundworks
        : setIsDownloadingRoadsAndSewers;
    setIsDownloading(true);
    try {
      if (jobTypeKey === "groundworks") {
        await downloadGroundworksTemplate();
      } else {
        await downloadRoadsAndSewersTemplate();
      }
      message.success("Take-off sheet downloaded successfully!");
    } catch (error) {
      console.error("Take-off download error:", error);
      message.error(
        "Failed to download take-off sheet. Please check your connection and try again.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadModalCancel = () => {
    setIsDownloadModalOpen(false);
  };

  const handleReviewCalculate = async (record: OpportunityRecord) => {
    // Clear historical version flag when reviewing a tender (editable mode)
    localStorage.removeItem("isHistoricalVersion");

    try {
      // Fetch tender data from API by project name
      message.loading({ content: "Loading tender data...", key: "loadTender", duration: 0 });

      const response = await billOfQuantitiesApi.getByProject(
        record.tender,
        record.opportunityNo,
      );

      // Handle different response structures
      let apiData = null;

      // Check if response has explicit success: false
      if (response && response.success === false) {
        message.error({
          content: response.message || "Failed to load tender data from API",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Extract data based on response structure
      if (response && response.data) {
        // Standard format: { success: true, data: PayloadFormat }
        apiData = response.data;
      } else if (response && response.project) {
        // Response is PayloadFormat directly (has project field)
        apiData = response;
      } else if (
        response &&
        typeof response === "object" &&
        Object.keys(response).length > 0
      ) {
        // Response is some data object
        apiData = response;
      }

      if (!apiData) {
        message.error({
          content: "No tender data found",
          key: "loadTender",
          duration: 3,
        });
        return;
      }

      // Convert PayloadFormat to TenderState for context update
      const tenderState = transformFromPayload(apiData, {
        id: record.tenderId,
      });

      // Check if there's actual data in the project and set job types
      let hasRoadsAndSewersData = false;
      let hasGroundworksData = false;

      // Check for Roads & Sewers data in the payload
      if (
        apiData.roadAndSewers ||
        apiData.preliminaryItems ||
        apiData.foulSewers ||
        apiData.stormSewers ||
        apiData.roads ||
        apiData.mainServices
      ) {
        hasRoadsAndSewersData = true;
      }

      // Check for Groundworks data in the payload
      if (
        apiData.groundworks ||
        (apiData.cutFills && Object.keys(apiData.cutFills).length > 0) ||
        (apiData.foundations && Object.keys(apiData.foundations).length > 0)
      ) {
        hasGroundworksData = true;
      }

      // Set job types in tenderState before dispatching
      tenderState.jobTypes = {
        roadsAndSewers: hasRoadsAndSewersData || record.rsBudget > 0,
        groundworks: hasGroundworksData || record.gwBudget > 0,
      };

      // Update TenderContext using dispatch
      dispatch({ type: "LOAD_PROJECT", payload: tenderState });

      // Save API data directly to localStorage (API data is already in PayloadFormat)
      const storageKey = `mjEvans_tender_${record.tenderId}`;
      const dataToStore = {
        version: "3.0",
        timestamp: new Date().toISOString(),
        data: apiData, // API data sits inside 'data' key
      };

      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      localStorage.setItem("currentTender", JSON.stringify(dataToStore));

      message.success({
        content: "Tender data loaded successfully",
        key: "loadTender",
        duration: 2,
      });
    } catch (error) {
      message.error({
        content: "Failed to load tender data from API",
        key: "loadTender",
        duration: 3,
      });
      return;
    }

    // Only show modal if both budgets have values
    if (record.rsBudget > 0 && record.gwBudget > 0) {
      setSelectedRecord(record);
      setActionType("review");
      setIsModalOpen(true);
      return;
    }

    // If only groundworks has value, navigate directly
    if (record.gwBudget > 0) {
      navigate("/groundworks/review");
      return;
    }

    // If only roads & sewers has value, navigate directly
    if (record.rsBudget > 0) {
      navigate("/roads-sewers/review");
      return;
    }

    // Default: navigate to groundworks review if both are 0
    navigate("/groundworks/review");
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const normalizeStatus = (status: string) => {
    const statusLower = status.toLowerCase();
    // Convert 'draft' to 'pending'
    if (statusLower === "draft") {
      return "pending";
    }
    return statusLower;
  };

  const handleHistory = (record: OpportunityRecord) => {
    const base = `/version-history/${encodeURIComponent(record.tender)}`;
    const qs = record.opportunityNo
      ? `?oppNo=${encodeURIComponent(record.opportunityNo)}`
      : "";
    navigate(`${base}${qs}`);
  };

  const handleDeleteClick = (record: OpportunityRecord) => {
    setDeletingRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;

    setIsDeleting(true);

    try {
      message.loading({ content: "Deleting tender...", key: "deleteTender", duration: 0 });

      // Delete all BOQ versions for this project first
      try {
        const versionHistory = await billOfQuantitiesApi.getVersionHistory(
          deletingRecord.tender,
          deletingRecord.opportunityNo,
        );
        if (versionHistory.versions && versionHistory.versions.length > 0) {
          await Promise.all(
            versionHistory.versions.map((v) =>
              billOfQuantitiesApi.deleteVersion(
                deletingRecord.tender,
                v.version,
                deletingRecord.opportunityNo,
              ),
            ),
          );
        }
      } catch (versionError) {
        // If versions fetch fails (e.g. 404 - no versions exist), continue with BOQ deletion
      }

      // Call API to delete by project name
      const response = await billOfQuantitiesApi.deleteByProject(
        deletingRecord.tender,
        deletingRecord.opportunityNo,
      );

      if (response.success) {
        // Remove from local storage
        const storageKey = `mjEvans_tender_${deletingRecord.tenderId}`;
        localStorage.removeItem(storageKey);

        // Invalidate React Query cache and wait for refetch to complete
        await queryClient.invalidateQueries({ queryKey: BOQ_QUERY_KEYS.all });

        message.success({
          content: "Tender deleted successfully",
          key: "deleteTender",
          duration: 2,
        });

        // Close modal and reset state after refetch completes
        setIsDeleting(false);
        setDeleteModalVisible(false);
        setDeletingRecord(null);
      } else {
        throw new Error(response.message || "Failed to delete tender");
      }
    } catch (error) {
      message.error({
        content: error.message || "Failed to delete tender",
        key: "deleteTender",
        duration: 3,
      });

      // Reset state on error
      setIsDeleting(false);
      setDeleteModalVisible(false);
      setDeletingRecord(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingRecord(null);
  };

  const handleRegionChange = (value: string) => {
    setSelectedRegion(value);
  };

  // Check if user can edit region (Board Level or System Administrator)
  const canEditRegion =
    userRole === "Board Level" || userRole === "System Administrator";

  // Regional Rate Library is System Administrator only
  const canViewRegionalRateLibrary = userRole === "System Administrator";

  const getStatusColor = (status: string) => {
    const statusLower = (status || "").toLowerCase().trim();

    // New BOQ workflow statuses
    if (statusLower === "in-progress") return "blue";
    if (statusLower === "under review - senior estimator") return "orange";
    if (statusLower === "under review commercial director") return "gold";
    if (statusLower === "under client review") return "green";
    if (statusLower === "won") return "purple";

    // Legacy statuses
    const statusNormalized = normalizeStatus(status);
    switch (statusNormalized) {
      case "approved":
        return "success";
      case "submitted":
        return "processing";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusStyle = (status: string) => {
    return { fontWeight: 600, fontSize: "13px" };
  };

  const getStatusDisplay = (status: string) => {
    const statusNormalized = normalizeStatus(status);
    return toPascalCase(statusNormalized);
  };

  const toPascalCase = (str: string) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const columns = [
    {
      title: "Opp. No",
      dataIndex: "opportunityNo",
      key: "opportunityNo",
      width: 120,
      render: (value: string | undefined) => value || "-",
    },
    {
      title: "Tender",
      dataIndex: "tender",
      key: "tender",
      width: 200,
      fixed: "left" as const,
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },

    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      width: 150,
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 140,
      render: (region: string) => (
        <Tag color="blue" style={{ fontWeight: 500 }}>
          {region}
        </Tag>
      ),
    },
    {
      title: "GW Budget",
      dataIndex: "gwBudget",
      key: "gwBudget",
      width: 130,
      align: "right" as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: "R&S Budget",
      dataIndex: "rsBudget",
      key: "rsBudget",
      width: 130,
      align: "right" as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: "Total Budget",
      dataIndex: "totalBudget",
      key: "totalBudget",
      width: 130,
      align: "right" as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 250,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={getStatusStyle(status)}>
          {getStatusDisplay(status)}
        </Tag>
      ),
    },
    {
      title: "Creator",
      dataIndex: "creator",
      key: "creator",
      width: 120,
      render: (creator: string) => (
        <span style={{ fontWeight: 500 }}>{toPascalCase(creator)}</span>
      ),
    },
    {
      title: "Creation Date",
      dataIndex: "creationDate",
      key: "creationDate",
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: "Last Update",
      dataIndex: "lastUpdateDate",
      key: "lastUpdateDate",
      width: 120,
      render: (date: string) => formatDate(date),
    },
    {
      title: "Last Modified By",
      dataIndex: "lastModifiedBy",
      key: "lastModifiedBy",
      width: 140,
      render: (lastModifiedBy: string) => (
        <span style={{ fontWeight: 500 }}>{toPascalCase(lastModifiedBy)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 140,
      render: (_: any, record: OpportunityRecord) => {
        return (
          <Space size="small">
            {/* <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              title="Edit Tender"
            ></Button>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
              title="View Tender"
            ></Button>
            <Button
              type="link"
              icon={<CalculatorOutlined />}
              onClick={() => handleReviewCalculate(record)}
              size="small"
              title="Calculate & Review"
            ></Button> */}
            <Button
              type="link"
              icon={<HistoryOutlined />}
              onClick={() => handleHistory(record)}
              size="small"
              title="Version History"
            ></Button>
            {!record.opportunityNo && (
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteClick(record)}
                size="small"
                title="Delete Tender"
              ></Button>
            )}
             
          </Space>
        );
      },
    },
  ];

  // Filter opportunities based on search, status, region, and "My Actions"
  const filteredOpportunities = useMemo(() => {
    const filtered = opportunities.filter((opportunity) => {
      // Search filter
      const matchesSearch = searchText
        ? opportunity.tender.toLowerCase().includes(searchText.toLowerCase()) ||
          opportunity.client.toLowerCase().includes(searchText.toLowerCase()) ||
          (opportunity.opportunityNo ?? "")
            .toLowerCase()
            .includes(searchText.toLowerCase())
        : true;

      // Status filter (case-insensitive comparison)
      const matchesStatus =
        statusFilter === "all" ||
        opportunity.status.toLowerCase() === statusFilter.toLowerCase();

      // Region filter - only show tenders from the user's selected/received region
      // If "All Regions" is selected, show all tenders regardless of region
      // Normalize both regions for comparison (trim and case-insensitive)
      const matchesRegion =
        !selectedRegion || selectedRegion.trim().toLowerCase() === "all regions"
          ? true // Show all if no region selected or "All Regions" selected
          : opportunity.region?.trim().toLowerCase() ===
            selectedRegion.trim().toLowerCase();

      // Job type filter
      const matchesJobType =
        jobTypeFilter === "all"
          ? true
          : jobTypeFilter === "roadsAndSewers"
            ? !!opportunity.jobTypes?.roadsAndSewers
            : !!opportunity.jobTypes?.groundworks;

      // "My Actions" filter based on role and status only (no creator check)
      let matchesMyActions = true;
      if (showMyActions && userRole) {
        const opportunityStatusLower = opportunity.status.toLowerCase().trim();

        // Role-based filtering logic - Match status with role
        // Show tenders that match the status relevant to the user's role
        switch (userRole) {
          case "Estimator":
            // Show tenders with status "In-Progress"
            matchesMyActions = opportunityStatusLower === "in-progress";
            break;

          case "Senior Estimator":
            // Show tenders with status "Under Review - Senior Estimator"
            matchesMyActions =
              opportunityStatusLower === "under review - senior estimator";
            break;

          case "Commercial Director":
            // Show tenders with status "Under Review Commercial Director"
            matchesMyActions =
              opportunityStatusLower === "under review commercial director";
            break;

          case "Board Level":
          case "System Administrator":
            // Show tenders with status "Under Client Review"
            matchesMyActions = opportunityStatusLower === "under client review";
            break;

          default:
            // For unknown roles, show no actions
            matchesMyActions = false;
            break;
        }
      }

      // Hide tenders created on these fixed calendar dates (permanent, not "today/yesterday").
      // Gated behind hideTenderDatesEnabled so builddev never filters these out.
      const matchesHiddenDate =
        !hideTenderDatesEnabled ||
        !HIDDEN_CREATION_DATES.includes(toLocalDateKey(opportunity.creationDate));

      return (
        matchesSearch &&
        matchesStatus &&
        matchesRegion &&
        matchesJobType &&
        matchesMyActions &&
        matchesHiddenDate
      );
    });

    // Sort by last-modified date descending (most recent first). `lastUpdateDate`
    // is stored as an ISO 8601 string, so a lexicographic compare matches
    // chronological order. Items missing a date sink to the bottom.
    const sorted = [...filtered].sort((a, b) => {
      const aDate = a.lastUpdateDate || "";
      const bDate = b.lastUpdateDate || "";
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return bDate.localeCompare(aDate);
    });

    return sorted;
  }, [
    opportunities,
    searchText,
    statusFilter,
    selectedRegion,
    jobTypeFilter,
    showMyActions,
    userRole,
  ]);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Navigation Bar - Matching tender-info page design */}
      <AntHeader
        className="bg-colorPrimary"
        style={{
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          padding: "12px 24px",
          height: "88px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              height: "100%",
            }}
          >
            <Link
              to="/"
              style={{
                display: "block",
                height: "64px",
                textDecoration: "none",
              }}
            >
              <img
                src={MJ_LOGO}
                alt="MNJ Construction Logo"
                style={{
                  height: "100%",
                  width: "auto",
                  maxWidth: "240px",
                  objectFit: "contain",
                }}
              />
            </Link>
            <span
              style={{
                color: "#e2e8f0",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Estimator
            </span>
            <EnvironmentBadge mode={import.meta.env.MODE} variant="dark" />
          </div>

          {/* User Profile and Logout */}
          <AuthButton textColor="#ffffff" backgroundColor="transparent" />
        </div>
      </AntHeader>

      {/* Main Content */}
      <div style={{ padding: "24px", backgroundColor: "#f5f5f5" }}>
        <Card>
          <div
            style={{
              marginBottom: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <Title level={3} style={{ margin: 0 }}>
              Tenders Dashboard
            </Title>
            <Space wrap size={[8, 8]}>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => navigate("/document-library")}
                size="large"
              >
                Document Library
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => setIsDownloadModalOpen(true)}
                onMouseEnter={() => setIsDownloadButtonHovered(true)}
                onMouseLeave={() => setIsDownloadButtonHovered(false)}
                size="large"
                style={{
                  backgroundColor: isDownloadButtonHovered
                    ? "#2b7a0b"
                    : "#389e0d",
                  borderColor: isDownloadButtonHovered
                    ? "#2b7a0b"
                    : "#389e0d",
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                Download Take-off
              </Button>
              {showDevMode && (
                <Button
                  icon={<TableOutlined />}
                  onClick={() => navigate("/takeoff-configuration")}
                  size="large"
                >
                  Takeoff Configuration
                </Button>
              )}
              {canViewRegionalRateLibrary && (
                <Button
                  icon={<CalculatorOutlined />}
                  onClick={() => navigate("/rate-engine/activity-tree")}
                  size="large"
                  style={{
                    backgroundColor: "#0891b2",
                    borderColor: "#0891b2",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                >
                  Regional Rate Library
                </Button>
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleNewOpportunity}
                size="large"
              >
                New Tender
              </Button>
            </Space>
          </div>

          {/* Region Selector - Only for Board Level and System Administrator */}
          {canEditRegion && (
            <div
              style={{
                marginBottom: "24px",
                padding: "16px",
                backgroundColor: "#f0f5ff",
                borderRadius: "8px",
                border: "1px solid #d6e4ff",
              }}
            >
              <Space align="center" size="middle">
                <EnvironmentOutlined
                  style={{ fontSize: "18px", color: "#1890ff" }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#262626",
                  }}
                >
                  Select Region:
                </span>
                <Select
                  style={{ width: "240px" }}
                  placeholder="Select your region"
                  value={selectedRegion || undefined}
                  onChange={handleRegionChange}
                  size="large"
                >
                  {AVAILABLE_REGIONS.map((reg) => (
                    <Select.Option key={reg} value={reg}>
                      {reg}
                    </Select.Option>
                  ))}
                </Select>
                {selectedRegion && (
                  <span style={{ fontSize: "13px", color: "#8c8c8c" }}>
                    Showing tenders for{" "}
                    <strong>
                      {selectedRegion === "All Regions"
                        ? "all regions"
                        : selectedRegion}
                    </strong>
                  </span>
                )}
              </Space>
            </div>
          )}

          {boqError && (
            <Alert
              type="warning"
              message="Unable to fetch Bill of Quantities data"
              description="Budget data is being calculated from local storage. Some budget information may be unavailable."
              style={{ marginBottom: "16px" }}
              showIcon
            />
          )}

          {/* Filters */}
          <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
            <Col xs={24} sm={12} md={5}>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => setShowMyActions(!showMyActions)}
                size="large"
                type={showMyActions ? "primary" : "default"}
                style={{
                  width: "100%",
                  ...(showMyActions
                    ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                    : {}),
                }}
              >
                {showMyActions ? "Show All" : "My Actions"}
              </Button>
            </Col>
            <Col xs={24} sm={12} md={7}>
              <Input
                placeholder="Search by tender name, client or opportunity no."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
                size="large"
              />
            </Col>
            {!showMyActions && (
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filter by status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Select.Option value="all">All Status</Select.Option>
                  <Select.Option value="In-progress">In-Progress</Select.Option>
                  <Select.Option value="Under Review - Senior Estimator">
                    Under Review - Senior Estimator
                  </Select.Option>
                  <Select.Option value="Under Review Commercial Director">
                    Under Review Commercial Director
                  </Select.Option>
                  <Select.Option value="Under Client Review">
                    Under Client Review
                  </Select.Option>
                  <Select.Option value="Won">Won</Select.Option>
                </Select>
              </Col>
            )}
            <Col xs={24} sm={12} md={showMyActions ? 12 : 6}>
              <Select
                placeholder="Filter by job type"
                value={jobTypeFilter}
                onChange={setJobTypeFilter}
                style={{ width: "100%" }}
                size="large"
              >
                <Select.Option value="all">All Job Types</Select.Option>
                <Select.Option value="roadsAndSewers">Roads & Sewers</Select.Option>
                <Select.Option value="groundworks">Groundworks</Select.Option>
              </Select>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={filteredOpportunities}
            loading={boqLoading}
            scroll={{ x: 1760 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} opportunities`,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, newPageSize) => {
                // If page size changed, reset to page 1
                if (newPageSize !== pageSize) {
                  setPageSize(newPageSize);
                  setCurrentPage(1);
                } else {
                  // Only page number changed, update it
                  setCurrentPage(page);
                }
              },
            }}
            bordered
            locale={{
              emptyText:
                opportunities.length === 0 && !boqLoading ? (
                  <div style={{ padding: "40px", textAlign: "center" }}>
                    <p
                      style={{
                        fontSize: "16px",
                        color: "#8c8c8c",
                        marginBottom: "16px",
                      }}
                    >
                      No opportunities found
                    </p>
                    <p style={{ fontSize: "14px", color: "#bfbfbf" }}>
                      Click 'New Tender' to create your first project
                    </p>
                  </div>
                ) : (
                  "No data"
                ),
            }}
          />
        </Card>
      </div>

      {/* Job Type Selection Modal */}
      <Modal
        title={`Select Job Type to ${
          actionType === "edit"
            ? "Edit"
            : actionType === "view"
              ? "View"
              : "Review"
        }`}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={500}
      >
        <div style={{ padding: "24px 0" }}>
          <p
            style={{ marginBottom: "24px", fontSize: "14px", color: "#595959" }}
          >
            Choose which section you want to{" "}
            {actionType === "edit"
              ? "edit"
              : actionType === "view"
                ? "view"
                : "review"}
            :
          </p>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Button
              type="primary"
              size="large"
              block
              icon={<Layers3 size={18} />}
              onClick={() => handleJobTypeSelect("groundworks")}
              style={{ height: "60px", fontSize: "16px" }}
            >
              Groundworks
            </Button>
            <Button
              type="primary"
              size="large"
              block
              icon={<Route size={18} />}
              onClick={() => handleJobTypeSelect("roadsAndSewers")}
              style={{ height: "60px", fontSize: "16px" }}
            >
              Roads & Sewers
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Download Take-off Modal */}
      <Modal
        title="Download Take-off Sheet"
        open={isDownloadModalOpen}
        onCancel={handleDownloadModalCancel}
        footer={null}
        width={500}
      >
        <div style={{ padding: "24px 0" }}>
          <Alert
            type="info"
            message="Select a take-off sheet to download"
            description="Choose which blank take-off template you want to download as an Excel file."
            showIcon
            style={{ marginBottom: "24px" }}
          />
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Button
              type="primary"
              size="large"
              block
              icon={<DownloadOutlined />}
              loading={isDownloadingGroundworks}
              onClick={() => handleDownloadTakeoff("groundworks")}
              style={{ height: "60px", fontSize: "16px" }}
            >
              Groundworks
            </Button>
            <Tooltip title="Roads & Sewers take-off sheet download is not available at the moment">
              <span style={{ display: "block" }}>
                <Button
                  type="primary"
                  size="large"
                  block
                  disabled
                  icon={<DownloadOutlined />}
                  loading={isDownloadingRoadsAndSewers}
                  style={{ height: "60px", fontSize: "16px" }}
                >
                  Roads & Sewers
                </Button>
              </span>
            </Tooltip>
          </Space>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
        title="Delete Tender"
        message={`Are you sure you want to delete "${deletingRecord?.tender}"? This action cannot be undone.`}
      />
    </Layout>
  );
};

export default OpportunitiesDashboard;
